import { useCallback, useEffect, useRef, useState } from 'react';
import {
    SPLIT_LAYOUT_EVENT,
    SPLIT_LAYOUT_KEY,
    normalizeSplitLayout,
    readSplitLayout,
    writeSplitLayout,
} from '../utils/splitLayout';

const same = (a, b) => a.expanded === b.expanded && a.order.join() === b.order.join();

/**
 * The Notes & Task List layout pref (#488): `{ order, expanded }` plus an
 * `update(fn)` that persists it and tells every other mounted copy (same tab via
 * SPLIT_LAYOUT_EVENT, other tabs via `storage`). The key + hook + change-event
 * triple every `omnis:*` pref follows.
 */
export default function useSplitLayout() {
    const [layout, setLayout] = useState(readSplitLayout);
    // The latest value, so `update` can compute + write OUTSIDE a state updater
    // (an updater must stay pure: StrictMode runs it twice).
    const layoutRef = useRef(layout);
    layoutRef.current = layout;

    // Who sent a change event: a copy ignores its own, and takes the layout from
    // the event rather than re-reading storage (which may be unavailable).
    const sourceRef = useRef(null);
    if (!sourceRef.current) sourceRef.current = {};

    useEffect(() => {
        const apply = (next) => { if (!same(next, layoutRef.current)) setLayout(next); };
        const onChange = (e) => {
            if (e.detail?.source === sourceRef.current) return;
            apply(e.detail?.layout ? normalizeSplitLayout(e.detail.layout) : readSplitLayout());
        };
        const onStorage = (e) => { if (e.key === SPLIT_LAYOUT_KEY || e.key === null) apply(readSplitLayout()); };
        window.addEventListener(SPLIT_LAYOUT_EVENT, onChange);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(SPLIT_LAYOUT_EVENT, onChange);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    const update = useCallback((fn) => {
        const prev = layoutRef.current;
        const next = normalizeSplitLayout(fn(prev));
        if (same(next, prev)) return;
        layoutRef.current = next;
        setLayout(next);
        writeSplitLayout(next);
        window.dispatchEvent(new CustomEvent(SPLIT_LAYOUT_EVENT, { detail: { layout: next, source: sourceRef.current } }));
    }, []);

    return [layout, update];
}
